import { ReactNode, useEffect, useRef } from 'react';
import styled from 'styled-components';
import {
  ClickAwayListener,
  Paper,
  Popover,
  PopoverOrigin,
  Popper,
  PopperPlacementType,
  PopperProps,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import Button from '../Button';
import { Flex, FlexColumn } from '../Flex';
import { fontSize, fontWeight, space, radius, shadow } from '../../style/utils';

const PanelSurface = styled(FlexColumn)`
  width: 320px;
  font-size: ${fontSize('md')};
  outline: none;
`;

const PanelHeader = styled(Flex)`
  align-items: center;
  gap: ${space('sm')};
  padding: ${space('md', 'lg')};
  border-bottom: 1px solid ${({ theme }) => theme.palette.border.muted};
  color: ${({ theme }) => theme.palette.content.secondary};
`;

const PanelHeaderTitle = styled(Typography)`
  font-size: ${fontSize('md')};
  font-weight: ${fontWeight('semibold')};
  color: ${({ theme }) => theme.palette.content.primary};
`;

const PanelFooter = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  padding: ${space('mdMinus', 'lg')};
  border-top: 1px solid ${({ theme }) => theme.palette.border.muted};
  background-color: ${({ theme }) => theme.palette.surface.muted};
`;

const FooterHint = styled(Typography)`
  font-size: ${fontSize('xs')};
  color: ${({ theme }) => theme.palette.content.secondary};
`;

export const DisplayPopoverSectionLabel = styled(Typography)`
  font-size: ${fontSize('xs')};
  font-weight: ${fontWeight('semibold')};
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${({ theme }) => theme.palette.content.disabled};
  padding: ${space('mdPlus', 'lg', 'smMinus')};
`;

export const DisplayPopoverOptionRow = styled(Flex)`
  align-items: center;
  gap: ${space('sm')};
  padding: ${space('smMinus', 'lg')};

  &:hover {
    background-color: ${({ theme }) => theme.palette.surface.tinted};
  }
`;

export const DisplayPopoverOptionLabel = styled(Typography).attrs({
  component: 'div',
})`
  flex: 1;
  font-size: ${fontSize('base')};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ theme }) => theme.palette.content.primary};
`;

export const DisplayPopoverChips = styled(Flex)`
  gap: ${space('smMinus')};
  padding: ${space('xs', 'lg', 'sm')};
`;

const DEFAULT_ANCHOR_ORIGIN: PopoverOrigin = {
  vertical: 'bottom',
  horizontal: 'right',
};

const DEFAULT_TRANSFORM_ORIGIN: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'right',
};

const ANCHOR_GAP_PX = 4;

/** Module-level so it stays the same object: Popper rebuilds its positioning
 * whenever `modifiers` changes, which briefly moves the panel to the corner. */
const POPPER_MODIFIERS: PopperProps['modifiers'] = [
  { name: 'offset', options: { offset: [0, ANCHOR_GAP_PX] } },
];

type PaperSxOverrides = { ml?: string; mt?: string; overflow?: 'hidden' };

const getPaperSx = (overrides: PaperSxOverrides): SxProps<Theme> => ({
  borderRadius: radius('lg'),
  border: '1px solid',
  borderColor: (theme) => theme.palette.border.divider,
  boxShadow: shadow('lg'),
  ...overrides,
});

type OpenSide = 'left' | 'right' | 'top' | 'bottom';

/** The side of the anchor the popover opens to. */
const getOpenSide = (
  anchorOrigin: PopoverOrigin,
  transformOrigin: PopoverOrigin,
): OpenSide => {
  if (
    anchorOrigin.horizontal === 'left' &&
    transformOrigin.horizontal === 'right'
  ) {
    return 'left';
  }
  if (
    anchorOrigin.horizontal === 'right' &&
    transformOrigin.horizontal === 'left'
  ) {
    return 'right';
  }
  if (
    anchorOrigin.vertical === 'top' &&
    transformOrigin.vertical === 'bottom'
  ) {
    return 'top';
  }
  return 'bottom';
};

/** Margin that leaves a gap from the anchor on the side the popover opens to. */
const ANCHOR_GAP_SX: Record<OpenSide, PaperSxOverrides> = {
  left: { ml: `-${ANCHOR_GAP_PX}px` },
  right: { ml: `${ANCHOR_GAP_PX}px` },
  top: { mt: `-${ANCHOR_GAP_PX}px` },
  bottom: { mt: `${ANCHOR_GAP_PX}px` },
};

const getPopperPlacement = (
  side: OpenSide,
  anchorOrigin: PopoverOrigin,
): PopperPlacementType => {
  const isEndAligned =
    side === 'left' || side === 'right'
      ? anchorOrigin.vertical === 'bottom'
      : anchorOrigin.horizontal === 'right';
  return `${side}-${isEndAligned ? 'end' : 'start'}`;
};

type Props = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  title: string;
  footerHint?: string;
  onReset?: () => void;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  /**
   * When false, the page behind stays interactive (scroll, zoom, drag) and
   * visible to screen readers, and the popover closes on Escape or on a click
   * outside it and outside `clickAwayIgnoredEl`.
   */
  isModal?: boolean;
  /** Should contain the trigger, so clicking it can toggle a non-modal popover. */
  clickAwayIgnoredEl?: Element | null;
  children: ReactNode;
};

const DisplayPopover = ({
  anchorEl,
  onClose,
  title,
  footerHint,
  onReset,
  anchorOrigin = DEFAULT_ANCHOR_ORIGIN,
  transformOrigin = DEFAULT_TRANSFORM_ORIGIN,
  isModal = true,
  clickAwayIgnoredEl,
  children,
}: Props) => {
  const isOpen = !!anchorEl;
  const isNonModalOpen = !isModal && isOpen;
  const surfaceRef = useRef<HTMLDivElement>(null);
  const side = getOpenSide(anchorOrigin, transformOrigin);

  useEffect(() => {
    if (isNonModalOpen) surfaceRef.current?.focus({ preventScroll: true });
  }, [isNonModalOpen]);

  useEffect(() => {
    if (!isNonModalOpen) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      onClose();
      anchorEl?.focus();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isNonModalOpen, onClose, anchorEl]);

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    if (
      event.target instanceof Node &&
      clickAwayIgnoredEl?.contains(event.target)
    ) {
      return;
    }
    onClose();
  };

  const panel = (
    <PanelSurface ref={surfaceRef} tabIndex={isModal ? undefined : -1}>
      <PanelHeader>
        <PanelHeaderTitle>{title}</PanelHeaderTitle>
      </PanelHeader>

      {children}

      {(footerHint || onReset) && (
        <PanelFooter>
          <FooterHint>{footerHint}</FooterHint>
          {onReset && (
            <Button variant="text" size="extraSmall" onClick={onReset}>
              Reset to defaults
            </Button>
          )}
        </PanelFooter>
      )}
    </PanelSurface>
  );

  if (isModal) {
    return (
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        PaperProps={{ sx: getPaperSx(ANCHOR_GAP_SX[side]) }}
      >
        {panel}
      </Popover>
    );
  }

  // A Popper rather than a non-modal Popover: Popover is a Modal underneath and
  // hides the rest of the page from screen readers while open.
  return (
    <Popper
      open={isOpen}
      anchorEl={anchorEl}
      placement={getPopperPlacement(side, anchorOrigin)}
      modifiers={POPPER_MODIFIERS}
      role="dialog"
      aria-label={title}
      sx={{ zIndex: (theme) => theme.zIndex.modal }}
    >
      {/* Pointer-down, not click: nested selects open on mouse-down, so the
          click's down/up targets differ and resolve to <body>, which would
          read as an outside click. Pointer events also cover touch taps. */}
      <ClickAwayListener
        onClickAway={handleClickAway}
        mouseEvent="onPointerDown"
        touchEvent={false}
      >
        <Paper sx={getPaperSx({ overflow: 'hidden' })}>{panel}</Paper>
      </ClickAwayListener>
    </Popper>
  );
};

export default DisplayPopover;
